/**
 * 프로젝트 차원 조회 상태 정책: **"0건"과 "못 가져옴"을 같은 화면으로 렌더하지 않는다.**
 * 두 상황이 요구하는 사용자 행동이 정반대이기 때문이다 — 0건은 생성·업로드, 실패는 재시도.
 * 실패를 빈 상태로 위장하면 사용자는 이미 가진 것을 다시 만들거나 다시 올린다.
 *
 * 쿼리 결과를 그리는 지점(목록·표·지도 마커·지도 레이어·파일 박스·선택 드롭다운)은
 * 판정을 손으로 다시 쓰지 않고 본 모듈의 `resolveQueryRenderState` 를 통해 갈래를 얻는다.
 * 계약 SoT 는 `query-failure-state-policy` 스펙.
 *
 * 본 모듈은 응답 봉투(`{ status, data }`)의 모양을 알지 못한다 — 봉투 실패는 리포지토리
 * 경계에서 `unwrapEnvelope` 로 오류로 승격되므로, 여기 도달하는 시점에는 실패 모드가
 * 하나뿐이다. 아직 승격되지 않은 쿼리를 위한 한시적 어댑터
 * (`withEnvelopeFailure`·`withEnvelopeFailureStatus`)만 예외로 두되, 그것도 봉투 모양이
 * 아니라 "봉투가 성공했는가" boolean 하나만 받는다.
 */

/** 렌더 갈래. 이 네 가지 외의 상태를 화면이 만들어내서는 안 된다. */
export type QueryRenderState = "pending" | "error" | "empty" | "ready";

/**
 * `pending` 의 사유. 화면이 "로딩 스피너"와 "아직 조건이 갖춰지지 않음" 안내를 나눠야 할 때 쓴다.
 * - `inactive`: 전제가 갖춰지지 않아 아직 조회하지 않음(react-query v4 는 `idle` 상태가 없어
 *   비활성 쿼리도 `status: "loading"` 으로 보고된다 — `fetchStatus` 로만 구분된다).
 * - `loading`: 실제로 조회 중.
 * - `placeholder`: placeholderData 로 채워져 있을 뿐 아무것도 종결되지 않음.
 */
export type PendingReason = "inactive" | "loading" | "placeholder";

export interface QueryRenderResult {
  state: QueryRenderState;
  /** `state === "pending"` 일 때만 채워진다. */
  pendingReason?: PendingReason;
  /**
   * 확보한 결과를 계속 보여주고 있지만 마지막 조회가 실패한 상태(`ready` 또는 `empty` 위에 얹힌다).
   * 이미 아는 사실이 있으므로 화면을 오류로 덮지 않고 국소 표시로만 알린다 — `empty` 위에 얹히는
   * 경우가 "성공했을 때 0건이었고 방금 재조회가 실패함"이다.
   */
  degraded: boolean;
}

export interface QueryRenderInput {
  /** react-query `UseQueryResult.status`. */
  status: "loading" | "error" | "success";
  /** react-query `UseQueryResult.fetchStatus`. */
  fetchStatus: "fetching" | "paused" | "idle";
  /** react-query `UseQueryResult.isPlaceholderData`. */
  isPlaceholderData?: boolean;
  /** 지금 화면에 표시할 수 있는 항목 수. 단일 객체 조회는 있으면 1, 없으면 0. */
  itemCount: number;
  /**
   * 이미 성공으로 종결된 결과를 확보했는가(react-query 의 `data !== undefined`).
   *
   * **필수다 — 기본값을 두지 않는다.** 기본값(`itemCount > 0`)을 두면 배선을 빠뜨린 지점이
   * 조용히 항목 수 기준으로 떨어져, 스펙이 금지한 판정이 컴파일도 테스트도 통과한다.
   * 필수로 두면 미배선이 타입 오류로 걸린다.
   *
   * 항목 수로 대신할 수 없는 이유: **0건이 정상인 목록**(접속자 0명인 현장 등)에서
   * "성공했는데 0건"과 "아직 아무것도 못 받음"이 같아지면, 폴링이 한 번 실패했을 뿐인데
   * 이미 알고 있던 "0건"이라는 사실을 버리고 오류 화면으로 덮는다.
   */
  hasData: boolean;
}

/**
 * 쿼리 상태와 표시 가능한 항목 수로 렌더 갈래를 판정한다.
 *
 * 판정 순서 자체가 계약이다:
 * 1. 실패로 종결 → **직전 성공 결과를 확보했으면** 그 결과를 계속 보여주고(degraded),
 *    아무것도 확보하지 못했으면 `error`.
 *    react-query 는 실패 상태에서도 직전 성공 데이터를 유지하므로, 이 2단이 없으면
 *    추가 페이지·배경 재조회 실패가 이미 보고 있던 목록을 지운다.
 * 2. 미종결(로딩·비활성·placeholder) → `pending`. 빈 상태도 오류도 렌더할 자격이 없다.
 * 3. 성공으로 종결 → 0건이면 `empty`, 아니면 `ready`.
 */
export function resolveQueryRenderState({
  status,
  fetchStatus,
  isPlaceholderData = false,
  itemCount,
  hasData,
}: QueryRenderInput): QueryRenderResult {
  if (status === "error") {
    // 확보한 결과가 "0건"이어도 그건 버릴 사실이 아니다 — 빈 상태를 유지한 채 실패만 국소로 알린다.
    if (hasData) {
      return { state: itemCount === 0 ? "empty" : "ready", degraded: true };
    }
    return { state: "error", degraded: false };
  }

  if (status === "loading") {
    return {
      state: "pending",
      pendingReason: fetchStatus === "idle" ? "inactive" : "loading",
      degraded: false,
    };
  }

  if (isPlaceholderData) {
    return { state: "pending", pendingReason: "placeholder", degraded: false };
  }

  return {
    state: itemCount === 0 ? "empty" : "ready",
    degraded: false,
  };
}

/**
 * **확보한 항목 수를 알고 있는가** — 성공 0건(`empty`)과 정상(`ready`) 갈래가 참이다.
 *
 * 이름이 재는 축 그대로다. 이 술어가 배제하는 것은 개수를 **모르는** 두 갈래(`pending` 의
 * 미종결, `error` 의 미확보)뿐이고 **마지막 조회의 성패는 묻지 않는다** — 직전 성공 결과를
 * 확보한 채 재조회만 실패한 `degraded` 는 개수를 이미 아는 상태라 참이다. 마지막 조회가
 * 성공으로 **종결**되었는지까지 물어야 하는 지점은 `isSettledForWrite` 를 쓴다.
 *
 * 쓰는 곳: 제목 옆 `(N)`·`0/0` 카운터처럼 개수를 단정해 그리는 지점. 미종결·실패 구간의
 * `0` 은 "0건"이 아니라 "모른다"이므로, 그 값을 단정으로 그리면 화면이 거짓말을 한다.
 */
export function isCountKnown(result: QueryRenderResult): boolean {
  return result.state === "empty" || result.state === "ready";
}

/**
 * 조회 결과를 **믿고 써도 되는가** — 쓰기 게이트다.
 *
 * `query-failure-state-policy` 스펙: 쓰기는 마지막 조회가 성공으로 종결된 경우에만 허용한다(SHALL).
 * 이름의 "settled" 는 그 **종결**을 뜻한다 — 개수를 아는 것(`isCountKnown`)보다 강한 조건이다.
 * 그래서 `isCountKnown` 에 더해 `degraded` 를 배제한다 — 목록을 계속 보여주는 것과, 그 목록이
 * 최신이라고 믿고 파일을 쓰는 것은 다른 판단이다. 마지막 조회가 실패로 끝났다면 그 사이
 * 올라온 항목을 모르는 상태이므로, 중복 회피처럼 목록의 **빠짐없음**을 전제하는 쓰기는
 * 근거가 사라진 채 실행된다(기존 동명 파일이 조용히 덮이는 형태로 드러난다).
 *
 * 반대로 이미 확보한 항목 하나를 고르는 쓰기(선택한 촬영일 등)는 완전성에 의존하지 않으므로
 * 이 술어를 쓰지 않는다 — `ExportFormSection` 의 `isTimeSeriesCountUnknown` 이 그 예다.
 */
export function isSettledForWrite(result: QueryRenderResult): boolean {
  return isCountKnown(result) && !result.degraded;
}

/**
 * 봉투 승격이 아직 적용되지 않은 쿼리의 **status 축**을 실패로 접는다.
 *
 * 봉투 실패(`{ status: "Failed" }`)는 전송으로는 성공이라 react-query 가 `success` 로 종결한다 —
 * 그대로 판정에 넘기면 확보한 결과가 없는데도 "성공 0건"(빈 상태)이나 무한 로딩으로 착지한다.
 * 아직 종결되지 않은 구간은 접지 않는다: 미도착 봉투를 실패로 읽으면 첫 로딩이 통째로 오류 화면이 된다.
 *
 * **한시적이다.** 리포지토리 경계가 `unwrapEnvelope` 로 승격되면(`query-failure-state-policy`
 * 의 1차 규범) 이 어댑터와 호출부의 `envelopeSucceeded` 배선을 함께 삭제한다.
 */
export function withEnvelopeFailureStatus(
  status: QueryRenderInput["status"],
  envelopeSucceeded: boolean
): QueryRenderInput["status"] {
  return status === "success" && !envelopeSucceeded ? "error" : status;
}

/**
 * 봉투 접기 어댑터가 읽고 되돌려주는 **상태 축**만 추린 형태.
 *
 * `UseQueryResult` 가 구조적으로 대입되므로 호출부는 쿼리 결과를 그대로 넘겨도 되지만,
 * **타입으로 읽을 수 있는 것은 이 세 축뿐이다.** 어댑터가 접는 것은 `status` 하나인데
 * `isError`·`error` 는 원본 값 그대로라, 접은 결과에까지 원본 모양을 남기면 어떤 쿼리도 만들
 * 수 없는 조합(`status: "error"` + `isError: false` + `error: null`)이 계약처럼 유통된다.
 *
 * **런타임 키 집합은 분기마다 다르다.** 좁아진 것은 타입뿐이라, 접지 않는 분기
 * (`withEnvelopeFailure` 가 받은 객체를 참조 그대로 돌려주는 쪽)에서는 넘긴 객체의 키가 그대로
 * 실려 나온다 — `UseQueryResult` 를 통째로 넘겼다면 그 전 필드가, 세 축만 골라 넘겼다면 그
 * 축들만. 새 객체가 되는 것은 접는 분기뿐이다. 타입이 가리는 것은 정적 접근이므로
 * `Object.keys`·스프레드·`JSON.stringify`(로깅)는 분기에 따라 다른 형태를 본다.
 * 재시도 핸들(`refetch`)처럼 접기와 무관한 필드가 필요한 소비처는 그 실려 나옴에 기대지 말고
 * **원본 쿼리에서 직접** 배선한다 — 그 배선이 코드에 보이면 무엇이 접혔고 무엇이 원본인지
 * 호출부에서 갈린다.
 */
export interface QueryStatusAxes {
  /** react-query `UseQueryResult.status`. */
  status: QueryRenderInput["status"];
  /** react-query `UseQueryResult.fetchStatus`. */
  fetchStatus: QueryRenderInput["fetchStatus"];
  /** react-query `UseQueryResult.isPlaceholderData`. */
  isPlaceholderData?: boolean;
}

/**
 * `withEnvelopeFailureStatus` 의 객체 형태 — 쿼리 결과를 그대로 넘기는 지점이 쓴다.
 *
 * 접지 않을 때는 **받은 객체를 그대로 반환한다**(참조 동일 — 불필요한 사본을 만들지 않는다).
 * 접을 때는 상태 축만 담은 **새 객체**를 만든다: 원본을 스프레드해 `status` 만 덮으면
 * 뒤집힌 상태 옆에 원본의 `isError: false` 가 실린 사본이 나간다.
 */
export function withEnvelopeFailure(
  query: QueryStatusAxes,
  envelopeSucceeded: boolean
): QueryStatusAxes {
  const status = withEnvelopeFailureStatus(query.status, envelopeSucceeded);
  if (status === query.status) return query;
  return {
    status,
    fetchStatus: query.fetchStatus,
    isPlaceholderData: query.isPlaceholderData,
  };
}

/**
 * 갈래별 렌더를 전수로 강제한다 — `branches` 가 `Record<QueryRenderState, ...>` 이므로
 * 갈래가 늘면 컴파일이 깨지고, 런타임에서 실제로 인덱싱하므로 강제력이 유지된다.
 * (선언만 하고 소비하지 않는 조건부 타입은 `never` 로 접혀도 통과하므로 가드가 되지 못한다.)
 */
export function matchQueryRenderState<T>(
  state: QueryRenderState,
  branches: Record<QueryRenderState, () => T>
): T {
  return branches[state]();
}
